/**
 * Query understanding for the job search.
 *
 * The search box used to hand Algolia one bag of words: query, location and
 * keyword boxes were joined with spaces and every term matched as text. Two
 * measured consequences (2026-09-08, against the live index):
 *
 *   "controller remote"  -> 180 hits, of which the 3rd, 5th and more were
 *                           is_remote=false. They matched the WORD "remote"
 *                           inside metro=remote_us or the location text, so
 *                           asking for remote returned office jobs.
 *   "controller"         -> "Cost Controller" and "Document Controller" (a
 *                           construction admin role) outranked real accounting
 *                           controllers, because nothing knew "controller" is
 *                           a canonical role we already classify.
 *
 * So: recognise what the reader typed before it reaches Algolia.
 *
 *   remote  -> a HARD filter (is_remote:true). It is a boolean we trust.
 *   role    -> a BOOST (optionalFilters). Not a filter, because filtering
 *              role:controller would drop "Assistant Controller", which is a
 *              result the reader wants.
 *   metro   -> a BOOST, for a stronger reason: metro_area is populated on only
 *              67% of live jobs and carries known mis-tags (66 "greater_denver"
 *              jobs are not in Colorado: Littleton MA, Golden NY, Tacoma WA).
 *              A hard metro filter would hide a third of the board and trust a
 *              field that is wrong often enough to notice.
 *
 * Matched words stay in the text query. Boosts re-rank, they do not restrict,
 * so stripping them would widen the search to everything.
 */
import { VOCAB_ROLES, VOCAB_METROS } from './searchVocab.gen';

export interface SearchChip { kind: 'role' | 'place' | 'remote'; label: string }

export interface SearchIntent {
  /** text to send to Algolia: the query minus any remote wording */
  text: string;
  /** canonical_roles.slug, when the query named a role we classify */
  role: string | null;
  /** mv_city_metros.slug, when the query named a metro */
  metro: string | null;
  /** true when the reader asked for remote work */
  remote: boolean;
  /** what we understood, for showing back to the reader */
  chips: SearchChip[];
}

const REMOTE_PHRASES = ['work from home', 'work from anywhere', 'fully remote', 'remote', 'wfh', 'telecommute'];

/** Places people type that are not the metro's short_name. */
const METRO_ALIASES: Record<string, string[]> = {
  greater_new_york: ['nyc', 'new york city', 'manhattan', 'brooklyn'],
  bay_area: ['sf', 'san francisco', 'silicon valley', 'palo alto', 'san jose', 'oakland'],
  greater_los_angeles: ['la', 'los angeles'],
  washington_dc: ['dc', 'washington', 'district of columbia'],
  twin_cities: ['minneapolis', 'st paul', 'saint paul', 'minneapolis st paul'],
  dallas_fort_worth: ['dfw', 'dallas', 'fort worth'],
  greater_toronto: ['gta', 'toronto'],
  saint_louis: ['st louis', 'saint louis'],
  raleigh_durham: ['raleigh', 'durham', 'research triangle'],
  hampton_roads: ['norfolk'],
  inland_empire: ['riverside', 'san bernardino'],
  orange_county: ['irvine', 'anaheim'],
};

type Entry = { kind: 'role' | 'place'; slug: string; label: string };

/** phrase -> entry, longest phrases win. Built once. */
const INDEX: Map<string, Entry> = (() => {
  const m = new Map<string, Entry>();
  const put = (phrase: string, e: Entry) => {
    const k = norm(phrase);
    // First writer wins, so a role's own display name is never overwritten by
    // another role's alias. Aliases genuinely collide ("tax analyst" is listed
    // under Tax Associate) and the canonical name is the better answer.
    if (k && k.split(' ').length <= 5 && !m.has(k)) m.set(k, e);
  };
  for (const r of VOCAB_ROLES) {
    put(r.d, { kind: 'role', slug: r.s, label: r.d });
    for (const a of r.a) put(a, { kind: 'role', slug: r.s, label: r.d });
  }
  for (const mt of VOCAB_METROS) {
    put(mt.n, { kind: 'place', slug: mt.s, label: mt.n });
    put(mt.s.replace(/^greater_/, '').replace(/_/g, ' '), { kind: 'place', slug: mt.s, label: mt.n });
    for (const a of METRO_ALIASES[mt.s] ?? []) put(a, { kind: 'place', slug: mt.s, label: mt.n });
  }
  return m;
})();

function norm(s: string): string {
  return s
    .toLowerCase()
    .replace(/&/g, ' and ')
    .replace(/[^a-z0-9\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

export function parseSearchIntent(raw: string): SearchIntent {
  const chips: SearchChip[] = [];
  let text = raw ?? '';
  let remote = false;

  // Remote wording is consumed: it becomes a filter, and leaving it in the text
  // is exactly what made "controller remote" return office jobs.
  for (const p of REMOTE_PHRASES) {
    const re = new RegExp(`(^|\\s)${p.replace(/ /g, '\\s+')}(\\s|$)`, 'ig');
    if (re.test(text)) { remote = true; text = text.replace(re, ' '); }
  }
  if (remote) chips.push({ kind: 'remote', label: 'Remote' });
  text = text.replace(/\s+/g, ' ').trim();

  // Greedy longest match over the remaining words. One role and one place is
  // all the ranking can use, so stop after the first of each.
  const words = norm(text).split(' ').filter(Boolean);
  let role: string | null = null;
  let metro: string | null = null;
  for (let i = 0; i < words.length && (!role || !metro); ) {
    let hit: Entry | null = null;
    let len = 0;
    for (let n = Math.min(5, words.length - i); n >= 1; n--) {
      const e = INDEX.get(words.slice(i, i + n).join(' '));
      if (e && ((e.kind === 'role' && !role) || (e.kind === 'place' && !metro))) { hit = e; len = n; break; }
    }
    if (!hit) { i += 1; continue; }
    if (hit.kind === 'role') { role = hit.slug; chips.push({ kind: 'role', label: hit.label }); }
    else { metro = hit.slug; chips.push({ kind: 'place', label: hit.label }); }
    i += len;
  }

  return { text, role, metro, remote, chips };
}
